import hive from '@hiveio/hive-js';
import { get } from 'lodash';
import { isLocal, symbolPack, unboxerAccount } from '../config/config';
import { onHandleNFTTransferAction } from './actions/nftMarketSell';
import { authenticateWithBE, sendTxAndBlockToBE } from './auth';
import { nap } from './generalUtils';
import { warmupLambdas } from './apis/warmup';


const hiveNodes = [
  'https://anyx.io',
  'https://api.hive.blog',
  'https://api.deathwing.me',
];

const pickHiveNode = () => {
  const randId = Math.floor(Math.random() * hiveNodes.length);
  const pickedNode = hiveNodes[randId];
  return pickedNode;
};

const setRandNode = () => {
  const pickedNode = pickHiveNode();
  console.log('picked Hive node:', pickedNode);
  hive.api.setOptions({ url: pickedNode });
};
setRandNode();


// ## USERNAME VALIDATION

export const validateUsername = ({ username }) => new Promise((resolve) => {
  // username format validation (dots, lenght, etc)
  const err = hive.utils.validateAccountName(username);
  if (err) {
    console.error('Invalid scholar name. Reason:', err);
    return resolve({ err });
  }
  // check account exists
  try {
    hive.api.getAccounts([username], (err, result) => {
      if (get(result, '[0].name') !== username) resolve({ err: 'account not found' });
      else resolve({});
    });
  } catch (err) {
    console.error('Caught err validating uname:', err);
    return ({ err });
  }
});


// ## UNBOXING

const dhive = require('@hiveio/dhive');
let client;
const SCAN_TIMEOUT = 2.8 * 60 * 1000;
const DELAY_BETWEEN_UNPACKS = 70 * 1000; // keep synched with api
let stream;

let startTime = 0;
let nftTransferDone = false;
let streamingStarted = false;
let pickedHiveNode;


const cleanupAfterScan = () => {
  isLocal && console.debug('cleaning up after scan');
  stream && stream.pause();
  stream = null; // TRY ALWAYS NEW STREAM TO SEE IF FEWER ISSUES
  clearTimeout(scanTimeoutTs);
};

export const transferNftAndListenChain = ({ nftId, user }) => new Promise(async (resolve) => {
  if ((Date.now() - startTime) < DELAY_BETWEEN_UNPACKS) {
    return resolve({ err: 'Please wait about a minute between unpacks', skipNotify: true });
  }
  // warmup lambdas to avoid timeouts
  warmupLambdas();

  nftTransferDone = false;
  startTime = Date.now();
  nftTransferDone = false;
  streamingStarted = false;
  pickedHiveNode = pickHiveNode();
  try {
    // 1. ## Start litening for tx ID and block #
    console.log('Streaming the chain to find NFT transfer..');
    listenForNFtTransferOnChain({ user, nftId, resolve });
    await nap(5000); // COLD BOOT WAIT TIME
    if (!streamingStarted) await nap(10 * 1000);
    if (!streamingStarted) {
      console.error('Hive scan did not start. Waited seconds:', (Date.now() - startTime) / 1000);
      return resolve({ err: `Hive scan start timeout for node ${pickedHiveNode}. Try again.` });
    }
    // 2. ## Transfer
    console.log('Unpacking box mint', nftId);
    onHandleNFTTransferAction(
      window.hive_keychain, user, [nftId].map(String),
      unboxerAccount, symbolPack,
      async (isOk) => {
        if (isOk) {
          nftTransferDone = true;
        } else {
          console.error('nft transfer failed.');
          resolve({ err: 'nft transfer ko' });
          cleanupAfterScan();
        }
      },
    );
  } catch (err) {
    alert(`Caught in unboxing: ${err} - ${JSON.stringify(err)}`);
    resolve({ err: 'caught error while unboxing' });
  }
});

let lostBlocks = 0;
let scanTimeoutTs;

const listenForNFtTransferOnChain = ({ user, nftId, resolve, useNewNode = false }) => {
  client = new dhive.Client(useNewNode ? pickHiveNode() : pickedHiveNode);
  // ## Handle Timeout
  scanTimeoutTs = setTimeout(() => {
    console.error('Killing stream. Took too long.');
    resolve({ err: nftTransferDone ? 'scan-timeout' : 'forgot-accept-transfer' });
    cleanupAfterScan();
  }, SCAN_TIMEOUT);
  // ## Active Streaming
  try {
    if (stream && !useNewNode) {
      isLocal && console.debug('<> re-using existing stream');
      stream.resume();
    } else {
      isLocal && console.debug('<> creating new stream');
      stream = client.blockchain.getBlockStream();
    }
    stream
      .on('data', async (block = { transactions: [] }) => {
        // isLocal && console.log(new Date(), '----- received block:', block);
        streamingStarted = true;
        const foundInfo = findNftTransfer({ block, user, nftId });
        if (foundInfo) {
          isLocal && console.debug('TRANSFER TX FOUND!', { foundInfo });
          // check no errors in HE - best effort: if error ko, if breaks ok
          const { err: heErrArr } = await heHasNoErrors({ tx: foundInfo.tx });
          if (heErrArr) {
            resolve({ err: heErrArr });
            cleanupAfterScan();
            return;
          }
          const seconds = (Date.now() - startTime) / 1000;
          const { tx, block } = foundInfo;
          cleanupAfterScan();
          resolve ({ tx, block, seconds });
        }
      })
      .on('error', async (err) => {
        console.log('Stream error:', err);
        if (err.message === 'Request Timeout') {
          isLocal && console.log('Node timeout, trying new stream with other node..');
          listenForNFtTransferOnChain({ user, nftId, resolve, useNewNode: true });
        }
        lostBlocks++;
        if (lostBlocks >= 5) {
          cleanupAfterScan();
          resolve({ err: 'Client missed too many blocks of the Hive blockchain' });
        }
      })
      .on('end', (arg) => console.log('Done streaming', arg));
  } catch (err) {
    console.error('Caught error starting stream:', err);
    resolve({ err: 'getBlockStream broke' });
  }
};

// TODO: add mainnet ID check @@
const findNftTransfer = ({ block = {}, user: sender, nftId } = {}) => {
  const foundInfo = block.transactions.map(
    (blockData = {}, txIndex) => {
      const currentBlockNum = blockData.block_num;
      const foundOp = get(blockData, 'operations', [])
      .find(([name = '', data = {} ] = []) => {
        // isLocal && console.debug(`Tx ${txIndex}/${block.transactions.length} of current block..`);
        if (!name || name !== 'custom_json') return false;
        if (data.required_auths[0] !== sender) return false;
        console.debug(`-> checking operation "${name}" of tx ${txIndex} with sender ${sender}..`);
          try {
            const parsedJson = JSON.parse(data.json);
            console.log('VALIDATING the custom json sent by the user:', { currentBlockNum, parsedJson, nftId });
            return isCsPackTransfer({ parsedJson, nftId });
          } catch (err) { console.error(`Error parsing custom json:`, { txIndex, name, data }); }
          return false;
        });
      if (foundOp) return ({ block: currentBlockNum, tx: get(block, `transaction_ids[${txIndex}]`) });
      return null;
    }
  ).filter(el => !!el)[0];
  foundInfo && console.debug('Find NFT transfer outcome ::::', { foundInfo });
  return foundInfo;
};

const isCsPackTransfer = ({ parsedJson, nftId }) => {
  // {"contractName":"nft","contractAction":"transfer","contractPayload":{"to":"crypto-shots","nfts":[{"symbol":"CSPACKS","ids":["1"]}]}}
  if (!parsedJson) return false;
  const { contractName, contractAction, contractPayload = {} } = parsedJson;
  if (contractName !== 'nft' || contractAction !== 'transfer') return false;
  const { to, nfts = [{}] } = contractPayload;
  if (!to || to !== unboxerAccount || !nfts[0] || !nfts[0].symbol) return false;
  const { symbol, ids = [] } = nfts[0];
  if (symbol !== 'CSPACKS' || +ids[0] !== nftId) return false;
  console.log('Found it!', { parsedJson });
  return true;
};



const heHasNoErrors = ({ tx }) => new Promise((resolve) => {
  console.log('Querying HE for tx', tx);
  try {
    const hiveEngineEndpoint = 'https://api.hive-engine.com/rpc/blockchain';
    fetch(hiveEngineEndpoint, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      referrer: 'https://he.dtools.dev/',
      body: `{"jsonrpc":"2.0","id":1,"method":"getTransactionInfo","params":{"txid":"${tx}"}}`,
      method: 'POST',
    })
      .then(res => res.json())
      .then(({ result = {} } = {}) => {
        isLocal && console.log('Data from HE:', result?.logs);
        try {
          const parsedJson = JSON.parse(result.logs);
          const { errors: errorsArr } = parsedJson || {};
          if (errorsArr && errorsArr.length) {
            console.error('Found errrors in HE:', { errorsArr });
            resolve({ err: errorsArr });
            return;
          }
          resolve({});
        } catch (err) {
          console.error('Error parsing custom json:', JSON.stringify({ result }));
          resolve({}); // best effort
        }
      })
  } catch (err) {
    console.error('Something broke fetching HE tx:', err);
    resolve({}); // best effort
  }
});


export const sendTxToBackend = async ({ user, tx, block }) => {
  // 3. ## BE authentication (JWT)
  try {
    console.log('Authenticating with Keychain and API..');
    const { err: errAuth } = await authenticateWithBE({ user });
    if (errAuth) return ({ err: 'The authentication required to claim packs failed' });
  } catch (err) {
    alert('Unable to authenticate. Are you on a stable connection? Trying again in 30 seconds..');
    await nap(30 * 1000);
    const { err: errAuthRetry } = await authenticateWithBE({ user });
    if (errAuthRetry) return ({ err: 'The authentication required to claim packs failed' });
  }
  // 3. ## Send Tx and Block to API (using JWT)
  try {
    console.log('Sending Tx to receive NFTs..');
    const { err: errClaimContents } = await sendTxAndBlockToBE({ user, tx, block });
    if (errClaimContents) return ({ err: errClaimContents });
  } catch (err) {
    alert('Unable to process the transaction. Are you on a stable connection? Trying again in 30 seconds..');
    await nap(30 * 1000);
    const { err: errClaimContentsRetry } = await sendTxAndBlockToBE({ user, tx, block });
    if (errClaimContentsRetry) return ({ err: errClaimContentsRetry });
  }
  console.log('success!');
  return({});
};
